import TitleLayout from '@/components/pages/create-webinar/TitleLayout';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { InputLabel } from '@/components/ui/InputLabel';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Title from '@/components/ui/Title';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Play } from 'lucide-react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  createWebhook,
  deleteWebhook,
  listWebhooks,
  publishWebinar,
  updateWebhook,
  testWebhook, // Nouveau Thunk
} from '@/features/webinar/webinarThunks';
import { webinarService } from '@/services/webinar.service';
import type { WebhookInput } from '@/features/webinar/types';
import Loader from '@/components/ui/Loader';
import { toast } from 'react-toastify';
import type { AppError } from '@/features/auth/types';
import { useTranslation } from 'react-i18next';
import { zHttpsUrlWithValidHostname } from '@/lib/validation/httpsIntegrationUrlSchema';
import { mapHttpError } from '@/lib/mapApiError';
import AutomationSection from '@/components/pages/create-webinar/integration/AutomationSection';

interface IntegrationSectionProps {
  currentWebinarId?: number;
  isEdit?: boolean;
}
export type WebhookEventType = 'ATTENDEE_CONNECTION' | 'ATTENDEE_END_STATS' | 'ATTENDEE_REGISTERED';

const MAX_WEBHOOKS_PER_EVENT = 3;

const ALL_WEBHOOK_TRIGGERS: WebhookEventType[] = [
  'ATTENDEE_CONNECTION',
  'ATTENDEE_END_STATS',
  'ATTENDEE_REGISTERED',
];

const EXISTING_WEBHOOK_SECRET_MASK = '••••••••••••';
const WEBHOOK_SECRET_TOO_LONG = 'HTTP.ERROR.400.WEBHOOK.SECRET.TOO_LONG';
const WEBHOOK_URL_INVALID = 'HTTP.ERROR.400.WEBHOOK.URL.INVALID';

function hasApiErrorCode(
  error: AppError & { details?: Array<{ message?: string; fieldName?: string }> },
  code: string,
): boolean {
  if (error.message === code) return true;
  return Array.isArray(error.details) && error.details.some((detail) => detail?.message === code);
}

type WebhookFormValues = {
  webhooks: Array<{
    id?: number;
    trigger: WebhookEventType;
    url: string;
    secret?: string;
    hasExistingSecret?: boolean;
  }>;
};

export default function IntegrationSection({ currentWebinarId, isEdit }: IntegrationSectionProps) {
  const { t } = useTranslation(['createWebinar', 'common']);
  const schema = useMemo(
    () =>
      z
        .object({
          webhooks: z
            .array(
              z.object({
                trigger: z.enum(
                  ['ATTENDEE_CONNECTION', 'ATTENDEE_END_STATS', 'ATTENDEE_REGISTERED'],
                  { message: t('integrationSection.webhooks.validation.triggerRequired') },
                ),
                url: zHttpsUrlWithValidHostname({
                  required: t('integrationSection.webhooks.validation.urlRequired'),
                  invalid: t('integrationSection.webhooks.urlInvalid'),
                  mustBeHttps: t('integrationSection.webhooks.validation.urlMustBeHttps'),
                  invalidDomain: t('integrationSection.webhooks.validation.urlInvalidDomain'),
                }),
                secret: z
                  .string()
                  .max(1024, t('integrationSection.webhooks.validation.secretMaxLength'))
                  .optional(),
                hasExistingSecret: z.boolean().optional(),
              }),
            )
            .superRefine((webhooks, ctx) => {
              const keyToIndices = new Map<string, number[]>();
              webhooks.forEach((webhook, index) => {
                const key = `${webhook.trigger}:${webhook.url.trim()}`;
                const indices = keyToIndices.get(key) ?? [];
                indices.push(index);
                keyToIndices.set(key, indices);
              });
              for (const indices of keyToIndices.values()) {
                if (indices.length <= 1) continue;
                for (const index of indices) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t('integrationSection.webhooks.validation.duplicateUrl'),
                    path: [index, 'url'],
                  });
                }
              }
            }),
        })
        .refine(
          (data) => {
            const counts = {
              ATTENDEE_CONNECTION: data.webhooks.filter((w) => w.trigger === 'ATTENDEE_CONNECTION')
                .length,
              ATTENDEE_END_STATS: data.webhooks.filter((w) => w.trigger === 'ATTENDEE_END_STATS')
                .length,
              ATTENDEE_REGISTERED: data.webhooks.filter((w) => w.trigger === 'ATTENDEE_REGISTERED')
                .length,
            };
            return Object.values(counts).every((c) => c <= MAX_WEBHOOKS_PER_EVENT);
          },
          {
            message: t('integrationSection.webhooks.validation.maxPerEvent', {
              max: MAX_WEBHOOKS_PER_EVENT,
            }),
            path: ['webhooks'],
          },
        ),
    [t],
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarId = currentWebinarId
    ? currentWebinarId
    : webinarIdParam
      ? parseInt(webinarIdParam, 10)
      : undefined;
  const existingWebhooks = useAppSelector((state) => state.webinar.webhooks);
  const webhooksLoading = useAppSelector((state) => state.webinar.webhooksLoading);
  const {
    currentWebinar,
    currentSessionConfiguration,
    currentWebinarSubscriptionPage,
    currentWaitingRoomPage,
  } = useAppSelector((state) => state.webinar);
  const isEvergreen = currentWebinar?.type === 'EVERGREEN';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testingId, setTestingId] = useState<number | null>(null);
  const hasInitialized = useRef(false);
  const [accordionValue, setAccordionValue] = useState<string>(
    isEdit ? '' : isEvergreen ? '' : 'item-1',
  );

  useEffect(() => {
    if (isEdit || !isEvergreen) return;
    setAccordionValue('');
  }, [isEdit, isEvergreen]);

  const form = useForm<WebhookFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      webhooks: [],
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'webhooks',
  });

  const watchedWebhooks = form.watch('webhooks');

  const canAddWebhook = useMemo(() => {
    const counts: Record<WebhookEventType, number> = {
      ATTENDEE_CONNECTION: 0,
      ATTENDEE_END_STATS: 0,
      ATTENDEE_REGISTERED: 0,
    };
    for (const w of watchedWebhooks) {
      if (w?.trigger) {
        counts[w.trigger]++;
      }
    }
    return ALL_WEBHOOK_TRIGGERS.some((t) => counts[t] < MAX_WEBHOOKS_PER_EVENT);
  }, [watchedWebhooks]);

  const handleAddWebhook = () => {
    const counts: Record<WebhookEventType, number> = {
      ATTENDEE_CONNECTION: 0,
      ATTENDEE_END_STATS: 0,
      ATTENDEE_REGISTERED: 0,
    };
    for (const w of watchedWebhooks) {
      if (w?.trigger) {
        counts[w.trigger]++;
      }
    }
    const availableTrigger = ALL_WEBHOOK_TRIGGERS.find((t) => counts[t] < MAX_WEBHOOKS_PER_EVENT);

    if (availableTrigger) {
      append({
        trigger: availableTrigger,
        url: '',
        secret: '',
        hasExistingSecret: false,
      });
    }
  };

  const getAvailableTriggers = (currentIndex: number): WebhookEventType[] => {
    const currentTrigger = watchedWebhooks[currentIndex]?.trigger;
    return ALL_WEBHOOK_TRIGGERS.filter((trigger) => {
      const countOthers = watchedWebhooks.filter(
        (w, i) => i !== currentIndex && w.trigger === trigger,
      ).length;
      return countOthers < MAX_WEBHOOKS_PER_EVENT || trigger === currentTrigger;
    });
  };

  useEffect(() => {
    if (workspaceId && webinarId && !isNaN(webinarId)) {
      dispatch(listWebhooks({ workspaceId, webinarId }));
    }
  }, [workspaceId, webinarId, dispatch]);

  useEffect(() => {
    if (!hasInitialized.current && existingWebhooks !== undefined) {
      if (existingWebhooks.length > 0) {
        form.reset({
          webhooks: existingWebhooks.map((wh) => {
            const hasExistingSecret = wh.hasSecret ?? Boolean(wh.secret?.trim());
            return {
              id: wh.id,
              trigger: wh.eventType as WebhookEventType,
              url: wh.url,
              secret: hasExistingSecret ? EXISTING_WEBHOOK_SECRET_MASK : '',
              hasExistingSecret,
            };
          }),
        });
      } else {
        form.reset({
          webhooks: [],
        });
      }

      hasInitialized.current = true;
    }
  }, [existingWebhooks, form]);

  const handleTestWebhook = async (id: number) => {
    if (!workspaceId || !webinarId) return;

    setTestingId(id);
    try {
      await dispatch(testWebhook({ workspaceId, webinarId, webhookId: id })).unwrap();
      toast.success(t('integrationSection.webhooks.testSuccess'));
    } catch (error) {
      const appError = error as AppError;
      const errorMessage = appError?.message ? t(appError.message) : t('errors.generic');
      toast.error(errorMessage);
    } finally {
      setTestingId(null);
    }
  };

  const handleSubmit = async (data: WebhookFormValues) => {
    if (!workspaceId || !webinarId || isNaN(webinarId)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const previous = await dispatch(listWebhooks({ workspaceId, webinarId })).unwrap();

      const formIds = new Set(
        data.webhooks
          .map((w) => w.id)
          .filter((id): id is number => id !== undefined && !Number.isNaN(id)),
      );

      for (const prev of previous) {
        if (!formIds.has(prev.id)) {
          await dispatch(deleteWebhook({ workspaceId, webinarId, webhookId: prev.id })).unwrap();
        }
      }

      for (const row of data.webhooks) {
        const trimmedSecret = row.secret?.trim();
        const isUnchangedExistingSecret =
          row.hasExistingSecret && trimmedSecret === EXISTING_WEBHOOK_SECRET_MASK;
        const secretToSend = isUnchangedExistingSecret ? undefined : trimmedSecret;
        const input: WebhookInput = {
          url: row.url.trim(),
          eventType: row.trigger,
          active: true,
          ...(secretToSend ? { secret: secretToSend } : {}),
        };

        if (row.id !== undefined && !Number.isNaN(row.id)) {
          const orig = previous.find((w) => w.id === row.id);
          const hasSecretUpdate = Boolean(secretToSend);
          if (
            orig &&
            (orig.url !== input.url || orig.eventType !== input.eventType || hasSecretUpdate)
          ) {
            await dispatch(
              updateWebhook({
                workspaceId,
                webinarId,
                webhookId: row.id,
                input,
              }),
            ).unwrap();
          }
        } else {
          await dispatch(createWebhook({ workspaceId, webinarId, input })).unwrap();
        }
      }

      hasInitialized.current = false;
      await dispatch(listWebhooks({ workspaceId, webinarId })).unwrap();
      toast.success(t('integrationSection.webhooks.saveSuccess'));
    } catch (error) {
      const appError = error as AppError & {
        details?: Array<{ message?: string; fieldName?: string }>;
      };

      if (hasApiErrorCode(appError, WEBHOOK_SECRET_TOO_LONG)) {
        toast.error(t(mapHttpError(WEBHOOK_SECRET_TOO_LONG)));
        return;
      }

      if (hasApiErrorCode(appError, WEBHOOK_URL_INVALID)) {
        toast.error(t('integrationSection.webhooks.urlInvalid'));
        return;
      }

      const errorMessage = appError?.message
        ? appError.message.startsWith('HTTP.')
          ? t(mapHttpError(appError.message))
          : t(appError.message)
        : t('errors.generic');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canPublish = useMemo(() => {
    if (!currentWebinar?.id) return false;
    if (!currentWaitingRoomPage) return false;
    if (!currentWebinarSubscriptionPage) return false;
    if (!currentSessionConfiguration) return false;
    return true;
  }, [
    currentWebinar,
    currentWaitingRoomPage,
    currentWebinarSubscriptionPage,
    currentSessionConfiguration,
  ]);

  const handlePublish = async () => {
    if (!webinarId || !workspaceId || !canPublish || isNaN(webinarId)) {
      return;
    }

    try {
      setIsSubmitting(true);
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        const templatesPage = await webinarService.listQuizTemplates(
          workspaceId,
          webinarId,
          page,
          50,
        );

        for (const template of templatesPage.content) {
          await webinarService.updateQuizTemplate(workspaceId, webinarId, template.id, {
            name: template.name,
            subTitle: template.subTitle,
            enabledDuringLive: template.enabledDuringLive,
            shuffleQuestions: template.shuffleQuestions,
            shuffleOptions: template.shuffleOptions,
            passingScore: template.passingScore,
            visibleFrom: template.visibleFrom,
            visibleUntil: template.visibleUntil,
            published: true,
            borderColor: template.borderColor,
            accentColor: template.accentColor,
            backgroundColor: template.backgroundColor,
            titleColor: template.titleColor,
            waitingRoomQuiz: template.waitingRoomQuiz,
            displayWaitingRoomQuiz: template.displayWaitingRoomQuiz,
          });
        }

        hasMore = page < templatesPage.totalPages - 1;
        page++;
      }

      const result = await dispatch(
        publishWebinar({
          workspaceId,
          id: webinarId,
        }),
      );

      if (publishWebinar.fulfilled.match(result)) {
        toast.success(t('integrationSection.publishSuccess'));
        navigate('/webinars');
      } else {
        toast.error(t('integrationSection.publishError'));
      }
    } catch (error) {
      toast.error(t('integrationSection.publishError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <TitleLayout title={t('integrationSection.title')} isEdit={isEdit}>
        <Accordion
          type="single"
          collapsible
          className="w-full"
          value={accordionValue}
          onValueChange={setAccordionValue}
        >
          <AccordionItem value="item-1" className="accordion-page p-0">
            <AccordionTrigger
              content={t('integrationSection.webhooks.rulesCount', {
                count: watchedWebhooks.length,
              })}
            >
              <div className="flex items-center gap-4">
                <img
                  src="/assets/webhooks.png"
                  alt={t('integrationSection.webhooks.title')}
                  className="size-19 rounded-lg"
                />
                <div className="space-y-1">
                  <Title level={2}>{t('integrationSection.webhooks.title')}</Title>
                  <p className="text-sm text-secondary">
                    {t('integrationSection.webhooks.description')}
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className=" flex flex-col gap-4">
              {fields.map((field, index) => {
                const availableTriggers = getAvailableTriggers(index);
                const dbId = watchedWebhooks[index]?.id;

                return (
                  <div key={field.id} className="flex flex-col gap-2">
                    <div className="flex items-start gap-2">
                      <div className="w-full">
                        <Controller
                          name={`webhooks.${index}.trigger`}
                          control={form.control}
                          render={({ field: triggerField }) => (
                            <Select
                              value={triggerField.value}
                              onValueChange={triggerField.onChange}
                            >
                              <SelectTrigger
                                label={t('integrationSection.webhooks.triggerLabel', {
                                  index: index + 1,
                                })}
                                className="class w-full h-14"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {availableTriggers.includes('ATTENDEE_CONNECTION') && (
                                  <SelectItem value="ATTENDEE_CONNECTION">
                                    {t('integrationSection.webhooks.triggers.attendeeConnection')}
                                  </SelectItem>
                                )}
                                {availableTriggers.includes('ATTENDEE_END_STATS') && (
                                  <SelectItem value="ATTENDEE_END_STATS">
                                    {t('integrationSection.webhooks.triggers.attendeeEndStats')}
                                  </SelectItem>
                                )}
                                {availableTriggers.includes('ATTENDEE_REGISTERED') && (
                                  <SelectItem value="ATTENDEE_REGISTERED">
                                    {t('integrationSection.webhooks.triggers.attendeeRegistered')}
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {form.formState.errors.webhooks?.[index]?.trigger && (
                          <p className="text-xs text-error mt-1">
                            {form.formState.errors.webhooks[index]?.trigger?.message}
                          </p>
                        )}
                      </div>
                      <div className="w-full">
                        <Controller
                          name={`webhooks.${index}.url`}
                          control={form.control}
                          render={({ field: urlField }) => (
                            <InputLabel
                              label={t('integrationSection.webhooks.urlLabel')}
                              className="w-full"
                              value={urlField.value}
                              onChange={urlField.onChange}
                            />
                          )}
                        />
                        {form.formState.errors.webhooks?.[index]?.url && (
                          <p className="text-xs text-error mt-1">
                            {form.formState.errors.webhooks[index]?.url?.message}
                          </p>
                        )}
                      </div>

                      {/* BOUTON TEST (PLAY) - Uniquement si déjà sauvegardé */}
                      {dbId && (
                        <button
                          type="button"
                          onClick={() => handleTestWebhook(dbId)}
                          disabled={testingId === dbId || isSubmitting}
                          className="bg-primary/10 text-primary rounded-lg py-3 px-3 border border-primary/20 cursor-pointer hover:bg-primary/20 h-14 flex items-center justify-center transition-colors"
                          title={t('integrationSection.webhooks.testTooltip')}
                        >
                          {testingId === dbId ? (
                            <Loader /> // Correction ici : pas de className
                          ) : (
                            <Play className="size-5 fill-current" />
                          )}
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="bg-white rounded-lg py-3 px-2 border border-input cursor-pointer hover:bg-gray-50 h-14 flex items-center justify-center"
                        title={t('integrationSection.webhooks.deleteTooltip')}
                      >
                        <Trash2 className="size-6" />
                      </button>
                    </div>
                    <div className="w-full">
                      <Controller
                        name={`webhooks.${index}.secret`}
                        control={form.control}
                        render={({ field: secretField }) => {
                          const hasExistingSecret = Boolean(
                            watchedWebhooks[index]?.hasExistingSecret,
                          );

                          return (
                            <InputLabel
                              label={
                                hasExistingSecret
                                  ? t('integrationSection.webhooks.secretConfigured')
                                  : t('integrationSection.webhooks.secretOptional')
                              }
                              type="password"
                              className="w-full"
                              value={secretField.value ?? ''}
                              onChange={(e) => {
                                let next = e.target.value;

                                if (hasExistingSecret) {
                                  if (next.startsWith(EXISTING_WEBHOOK_SECRET_MASK)) {
                                    next = next.slice(EXISTING_WEBHOOK_SECRET_MASK.length);
                                  } else {
                                    next = next.replaceAll(EXISTING_WEBHOOK_SECRET_MASK, '');
                                  }
                                  form.setValue(`webhooks.${index}.hasExistingSecret`, false);
                                }

                                secretField.onChange(next);
                              }}
                              autoComplete="new-password"
                              hidePasswordToggle={hasExistingSecret}
                            />
                          );
                        }}
                      />
                    </div>
                  </div>
                );
              })}

              {canAddWebhook && (
                <button
                  type="button"
                  onClick={handleAddWebhook}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Plus className="size-6" />
                  <Label className="cursor-pointer">
                    {t('integrationSection.webhooks.addWebhook')}
                  </Label>
                </button>
              )}

              {form.formState.errors.webhooks &&
                typeof form.formState.errors.webhooks.message === 'string' && (
                  <p className="text-xs text-error">{form.formState.errors.webhooks.message}</p>
                )}

              <Button
                type="button"
                onClick={form.handleSubmit(handleSubmit)}
                disabled={isSubmitting || webhooksLoading}
                className="w-full"
              >
                {isSubmitting && <Loader />}
                {t('save')}
              </Button>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <AutomationSection webinarId={webinarId} />
      </TitleLayout>

      <div className="mx-6">
        {!isEdit && (
          <Button
            className="w-full mt-6"
            onClick={handlePublish}
            disabled={isSubmitting || !canPublish}
          >
            {isSubmitting && <Loader />}
            {t('integrationSection.publish')}
          </Button>
        )}
      </div>
    </div>
  );
}
